import React, { useState, useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { chatMarkdownComponents } from './utils/chatTable';
import { getAuthHeaders, appendTokenToUrl, API_BASE_URL, interceptProtectedLinkClick } from './auth';
import { encodeWAV } from './utils/audioUtils';
import { alignNumericColumns } from './utils/markdownTables';
import { chatBubbleSx, chatRowSx, ModalHeader, closeOnlyViaButton, BotAvatar, EmailDraftCard } from './chatMessageStyles';

// MUI components
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Slide from '@mui/material/Slide';

// MUI Icons
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SendIcon from '@mui/icons-material/Send';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SecurityIcon from '@mui/icons-material/Security';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FactoryIcon from '@mui/icons-material/Factory';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const ChatWidget = ({ language }) => {
  const [copyIcon, setCopyIcon] = useState('copy');
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const chatBodyRef = useRef(null);
  const currentBotMessage = useRef('');
  const [isRecording, setIsRecording] = useState(false);
  const audioContextRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const processorRef = useRef(null);
  const audioBufferRef = useRef([]);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isPerformanceOpen, setIsPerformanceOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const copyLastBotMessageToClipboard = () => {
    const lastBotMessage = messages.slice().reverse().find(m => m.sender === 'bot');
    if (lastBotMessage) {
      const htmlContent = marked(lastBotMessage.text);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      navigator.clipboard.write([clipboardItem])
        .then(() => {
          setCopyIcon('check');
          setTimeout(() => setCopyIcon('copy'), 1000);
        })
        .catch(err => console.error('Could not copy to clipboard', err));
    }
  };

  const modifyLinks = (htmlText) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    doc.querySelectorAll('a').forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    return doc.body.innerHTML;
  };

  // alignNumericColumns first: `marked` only right-aligns a column when the
  // markdown carries `---:`, which the model frequently omits.
  const renderMessage = (text) => modifyLinks(marked.parse(alignNumericColumns(text)));

  // theScreener report links in an answer need the login token, which a plain
  // link can't send: one delegated handler on the message body opens them
  // authenticated (errors via the default alert — the widget has no snackbar).
  const handleProtectedLinkClick = useCallback((event) => {
    interceptProtectedLinkClick(event);
  }, []);

  const fetchWelcomeMessages = useCallback((sid) => {
    const eventSource = new EventSource(appendTokenToUrl(`/api/welcome_message?session_id=${sid}&language=${language}`));
    let welcome = '';
    let isFirstChunk = true;

    eventSource.onmessage = (event) => {
      const chunk = event.data;
      if (chunk !== 'end of response') {
        const parsed = chunk
          .replace(/<newline>/g, '\n')
          .replace(/\bhochgestuftem\b/g, '<span style="color:green;">hochgestuftem</span>')
          .replace(/\bheruntergestuftem\b/g, '<span style="color:red;">heruntergestuftem</span>');
        welcome += parsed;

        if (isFirstChunk) { setIsLoading(false); isFirstChunk = false; }

        setMessages(prev => {
          if (prev.length > 0 && prev[0].sender === 'bot') {
            const updated = [...prev];
            updated[0] = { ...updated[0], text: welcome };
            return updated;
          }
          return [{ text: welcome, sender: 'bot' }, ...prev];
        });
        setWelcomeMessage(welcome);
      }
    };

    eventSource.onerror = () => { setIsLoading(false); eventSource.close(); };

    return () => eventSource.close();
  }, [language]);

  const generateSessionId = () => `${Date.now()}-${Math.floor(Math.random() * 10000) + 1}`;

  const toggleChat = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setTimeout(() => setIsVisible(true), 0);
      document.body.classList.add('no-scroll');
      if (!isInitialized) setIsInitialized(true);
    } else {
      setIsVisible(false);
      setTimeout(() => setIsOpen(false), 400);
      document.body.classList.remove('no-scroll');
    }
  }, [isOpen, isInitialized]);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;
    const userMessage = { text: inputMessage, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);
    currentBotMessage.current = '';

    try {
      const eventSource = new EventSource(appendTokenToUrl(`/api/chat?user_input=${encodeURIComponent(messageToSend)}&session_id=${sessionId}&language=${language}&customer_name=${encodeURIComponent('Memo Dener')}`));

      eventSource.onmessage = (event) => {
        const chunk = event.data;
        if (chunk !== 'end of response') {
          setIsLoading(false);
          currentBotMessage.current += chunk.replace(/<newline>/g, '\n');
          setMessages(msgs => {
            const last = msgs[msgs.length - 1];
            // An email-draft card is its own message — stream any following
            // announce-text into a fresh bubble instead of mutating the card.
            if (last && last.sender === 'bot' && !last.emailDraft) {
              return [...msgs.slice(0, -1), { ...last, text: currentBotMessage.current }];
            }
            return [...msgs, { text: currentBotMessage.current, sender: 'bot' }];
          });
        } else {
          eventSource.close();
        }
      };

      eventSource.onerror = () => {
        setMessages(prev => [...prev, { text: 'Failed to get response from server.', sender: 'bot' }]);
        setIsLoading(false);
        eventSource.close();
      };

      // Structured email draft (same event the expandable Assistant renders):
      // append it as its own bot message carrying the draft object, so the
      // review card with the "E-Mail öffnen" button shows in the widget too.
      eventSource.addEventListener('email_draft', (event) => {
        try {
          const draft = JSON.parse(event.data);
          if (draft && draft.mailto) {
            setIsLoading(false);
            setMessages(msgs => [...msgs, { sender: 'bot', emailDraft: draft, text: `E-Mail-Entwurf an ${draft.recipient_name || ''}\nBetreff: ${draft.subject || ''}\n\n${draft.body || ''}` }]);
            // The streamed announce-text that follows must start a NEW bubble,
            // not overwrite the draft card.
            currentBotMessage.current = '';
          }
        } catch (e) {
          console.warn('Failed to parse email_draft event:', e);
        }
      });

      eventSource.addEventListener('end', () => {
        setMessages(msgs => {
          const last = msgs[msgs.length - 1];
          if (last && last.sender === 'bot' && !last.emailDraft) {
            return [...msgs.slice(0, -1), { ...last, text: currentBotMessage.current }];
          }
          return msgs;
        });
        setIsLoading(false);
        eventSource.close();
      });
    } catch (error) {
      console.error('Error fetching response:', error);
      setMessages(prev => [...prev, { text: 'Failed to get response from server.', sender: 'bot' }]);
      setIsLoading(false);
    }
  };

  const generateWordDocument = async (msgs) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate_word_document`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ messages: msgs }),
      });
      if (!response.ok) throw new Error('Failed to generate document');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ChatHistory.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error generating document:', error);
    }
  };

  const handleRecordClick = async () => {
    if (isRecording) {
      // Capture the actual capture rate before closing the context; the WAV
      // header must match the native rate the ScriptProcessor recorded at.
      const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100;
      if (processorRef.current && mediaStreamSourceRef.current) {
        mediaStreamSourceRef.current.disconnect();
        processorRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (mediaStreamSourceRef.current && mediaStreamSourceRef.current.mediaStream) {
        mediaStreamSourceRef.current.mediaStream.getTracks().forEach(track => track.stop());
      }
      setIsRecording(false);
      processAudioData(audioBufferRef.current, sampleRate);
    } else {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio recording.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        mediaStreamSourceRef.current = audioContext.createMediaStreamSource(stream);
        processorRef.current = audioContext.createScriptProcessor(4096, 1, 1);
        mediaStreamSourceRef.current.connect(processorRef.current);
        processorRef.current.connect(audioContext.destination);
        audioBufferRef.current = [];
        processorRef.current.onaudioprocess = (e) => {
          audioBufferRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        };
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access your microphone. Please check permissions.');
      }
    }
  };

  const processAudioData = async (buffer, sampleRate = 44100) => {
    setIsAudioLoading(true);
    const wavBlob = encodeWAV(buffer, sampleRate);
    const reader = new FileReader();
    reader.readAsDataURL(wavBlob);
    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];
      try {
        const response = await fetch(`${API_BASE_URL}/api/translate_audio_to_text`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ audio_file: base64data }),
        });
        if (!response.ok) throw new Error('Failed to process audio');
        const data = await response.json();
        if (data && data.user_input) setInputMessage(data.user_input);
        else console.error('Invalid response from API');
      } catch (error) {
        console.error('Error processing audio:', error);
      } finally {
        setIsAudioLoading(false);
      }
    };
  };

  // What the last greeting fetch was for. The widget has no "clear chat", so the
  // greeting is the one bubble that follows the app language — but re-running
  // this effect used to blank the transcript first, taking the conversation with
  // it. Within one open session fetchWelcomeMessages rewrites messages[0] in
  // place instead, so the turns below it survive. Opening the widget or starting
  // a new session still begins from a clean slate, unchanged.
  const greetedRef = useRef({ open: false, sessionId: null });
  useEffect(() => {
    if (!isOpen || !isInitialized) {
      greetedRef.current = { open: false, sessionId: null };
      return undefined;
    }
    const sameOpenSession = greetedRef.current.open && greetedRef.current.sessionId === sessionId;
    greetedRef.current = { open: true, sessionId };
    setIsLoading(true);
    if (!sameOpenSession) setMessages([{ text: '', sender: 'bot' }]);
    const cleanup = fetchWelcomeMessages(sessionId);
    return () => { if (cleanup) cleanup(); };
  }, [isOpen, isInitialized, language, sessionId, fetchWelcomeMessages]);

  useEffect(() => { setSessionId(generateSessionId()); }, []);

  useEffect(() => {
    const openTimer = setTimeout(() => {
      setIsOpen(true);
      setTimeout(() => { setIsVisible(true); setIsInitialized(true); }, 50);
    }, 500);
    return () => clearTimeout(openTimer);
  }, []);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Chat window dimensions
  const chatWidth = isExpanded ? 650 : 380;
  const chatHeight = isExpanded ? 850 : 600;

  return (
    <>
      {/* Floating action button — fully hidden when chat is open */}
      {!isOpen && !isVisible && (
        <Fab
          color="primary"
          onClick={toggleChat}
          sx={{
            position: 'fixed',
            bottom: 25,
            right: 25,
            zIndex: 999998,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.1)' },
            },
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      {/* Chat window */}
      {isOpen && (
        <>
          {/* Mobile overlay */}
          <Box
            onClick={toggleChat}
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.65)',
              zIndex: 999999,
            }}
          />

          <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: { xs: '1%', md: 24 },
                right: { xs: '4%', md: 24 },
                width: { xs: '92%', md: chatWidth },
                height: { xs: '90%', md: chatHeight },
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10000000,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 40px rgba(25, 208, 239, 0.2), 0 0 80px rgba(25, 208, 239, 0.08)',
                transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1), height 0.4s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.surface || theme.palette.background.default} 100%)`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  color: 'text.primary',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Early Lion AI
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Portfolio Performance">
                    <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} onClick={() => setIsPerformanceOpen(true)}>
                      <TrendingUpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hilfe">
                    <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} onClick={() => setIsHelpOpen(true)}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isExpanded ? 'Verkleinern' : 'Vergr\u00f6ssern'}>
                    <IconButton
                      size="small"
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, display: { xs: 'none', md: 'inline-flex' } }}
                      onClick={() => setIsExpanded(prev => !prev)}
                    >
                      {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Letzte Antwort kopieren">
                    <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} onClick={copyLastBotMessageToClipboard}>
                      {copyIcon === 'check' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Chatverlauf in Word exportieren">
                    <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} onClick={() => generateWordDocument(messages.slice(1))}>
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} onClick={toggleChat}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Message body */}
              <Box
                ref={chatBodyRef}
                onClick={handleProtectedLinkClick}
                onAuxClick={handleProtectedLinkClick}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.default',
                }}
              >
                {messages.map((message, index) => {
                  const isUser = message.sender === 'user';
                  return (
                    <Box key={index} sx={chatRowSx(isUser)}>
                      {!isUser && <BotAvatar />}
                      <Box sx={chatBubbleSx(isUser)}>
                        {message.emailDraft ? (
                          <EmailDraftCard draft={message.emailDraft} />
                        ) : message.sender === 'bot' ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={chatMarkdownComponents}>
                            {renderMessage(message.text)}
                          </ReactMarkdown>
                        ) : (
                          message.text
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={18} />
                  </Box>
                )}
              </Box>

              {/* Footer */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1.5, bgcolor: 'background.default' }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mb: 0.5,
                    color: 'text.secondary',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  onClick={() => window.open('https://www.thescreener.com/', '_blank')}
                >
                  Powered with data by theScreener
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isAudioLoading && <CircularProgress size={18} sx={{ mr: 0.5 }} />}
                  <TextField
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter sends, Shift+Enter inserts a line break (multiline input).
                      if (e.key === 'Enter' && !e.shiftKey && !isAudioLoading) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={isAudioLoading}
                    placeholder={isAudioLoading ? '' : 'Type your message...'}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    maxRows={6}
                    sx={{
                      '& .MuiOutlinedInput-root': { fontSize: 'body2.fontSize' },
                    }}
                  />
                  <IconButton
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    color="primary"
                    size="small"
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleRecordClick}
                    disabled={isLoading}
                    size="small"
                    sx={{ color: isRecording ? 'error.main' : 'text.secondary' }}
                  >
                    {isRecording ? <FiberManualRecordIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </Slide>
        </>
      )}

      {/* Help Dialog */}
      <Dialog open={isHelpOpen} onClose={closeOnlyViaButton(() => setIsHelpOpen(false))} maxWidth="sm" fullWidth>
        <Box sx={{ px: 3, pt: 2 }}>
          <ModalHeader title="Earlylion AI Hilfe" icon={<HelpOutlineIcon />} onClose={() => setIsHelpOpen(false)} />
        </Box>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Sie k&ouml;nnen Fragen stellen wie:</Typography>

          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
            <TrendingUpIcon color="primary" fontSize="small" /> Portfolios:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Wie ist die Performance von meinem Portfolio?</li>
          </Typography>

          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
            <SecurityIcon color="primary" fontSize="small" /> Positionen:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Wie beurteilen die Experten das Risiko von ABB?</li>
            <li>Was sind die Top 5 Aktien des Stoxx 600?</li>
            <li>Ich suche nach guten Dividendentiteln, was kannst du mir empfehlen?</li>
          </Typography>

          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
            <SwapHorizIcon color="primary" fontSize="small" /> Alternativen:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Gib mir Alternativen zu Kering.</li>
          </Typography>

          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
            <FactoryIcon color="primary" fontSize="small" /> Sektoren:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Gib mir Informationen zum Nahrungsmittel Sektor.</li>
          </Typography>

          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
            <PictureAsPdfIcon color="primary" fontSize="small" /> theScreener Reports:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 1 }}>
            <li>Ich m&ouml;chte den PDF Report von Roche sehen.</li>
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Portfolio Performance Dialog */}
      <Dialog open={isPerformanceOpen} onClose={closeOnlyViaButton(() => setIsPerformanceOpen(false))} maxWidth="sm" fullWidth>
        <Box sx={{ px: 3, pt: 2 }}>
          <ModalHeader title="Portfolio Performance" icon={<TrendingUpIcon />} onClose={() => setIsPerformanceOpen(false)} />
        </Box>
        <DialogContent
          sx={{
            maxHeight: '80vh',
            color: 'text.primary',
            '& p': { fontSize: '13px', lineHeight: 1.7, color: 'text.secondary', m: 0, mb: 1, '&:last-child': { mb: 0 } },
            '& strong, & b': { color: 'text.primary', fontWeight: 600 },
            '& em, & i': { color: 'primary.main' },
            '& a': { color: 'primary.main', textDecoration: 'none' },
            '& ul, & ol': { pl: 2.5, my: 0.5, '& li': { color: 'text.secondary', fontSize: '13px', lineHeight: 1.7 }, '& li::marker': { color: 'primary.main' } },
            '& h1, & h2, & h3': { color: 'primary.main', fontSize: '15px', fontWeight: 600, mt: 2, mb: 1 },
            '& table': {
              width: '100%', borderCollapse: 'separate', borderSpacing: 0, my: 1,
              borderRadius: '8px', overflow: 'hidden', border: '1px solid', borderColor: 'divider',
              '& th': { bgcolor: 'rgba(255,255,255,0.06)', fontWeight: 600, fontSize: '12px', p: 1, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' },
              '& td': { fontSize: '12px', p: 1, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'rgba(255,255,255,0.04)' },
              '& tr:last-child td': { borderBottom: 'none' },
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={chatMarkdownComponents}>
            {renderMessage(welcomeMessage)}
          </ReactMarkdown>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatWidget;
